import * as XLSX from 'xlsx';
import { MONTHS, getMesesDoPeriodo } from './periodoUtils';

export { MONTHS };

function findHeaderRowIndex(data) {
    for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const hasCliente = row.some(c => String(c).trim() === 'Cliente');
        const hasOferta  = row.some(c => String(c).trim() === 'Oferta');
        if (hasCliente && hasOferta) return i;
    }
    return -1;
}

function buildColIndex(headerRow) {
    const idx = {};
    headerRow.forEach((cell, i) => {
        const key = String(cell).trim().toLowerCase();
        idx[key] = i;
    });
    return idx;
}

function getCol(row, colIdx, name) {
    const i = colIdx[name.toLowerCase()];
    return i !== undefined ? row[i] : '';
}

function parseRow(row, colIdx, rowIndex) {
    const meses = {};
    MONTHS.forEach(({ key }) => {
        const i = colIdx[key.toLowerCase()];
        meses[key] = i !== undefined ? (Number(row[i]) || 0) : 0;
    });

    // Julio/Julho: the header uses "Julho" with capital J
    // buildColIndex lowercases everything so 'julho' will match

    return {
        _id: `row-${rowIndex}`,
        selected: false,
        cliente:       String(getCol(row, colIdx, 'cliente')       || '').trim(),
        categoria:     String(getCol(row, colIdx, 'suboferta')      || '').trim(),
        probabilidade: Number(getCol(row, colIdx, 'probabilidade'))  || 0,
        gp:            String(getCol(row, colIdx, 'gp')             || '').trim(),
        meses,
        somaTotal:     Number(getCol(row, colIdx, 'valor total ano')) || 0,
        somaPonderada: Number(getCol(row, colIdx, 'soma ponderada')) || 0,
        margemLiquida: Number(getCol(row, colIdx, '% lucro líquido')) || 0,
        // kept for reference only, not shown in table
        oferta:        String(getCol(row, colIdx, 'oferta')         || '').trim(),
    };
}

/**
 * Reads an XLSX File object and returns filtered forecast rows.
 * Filters: Oferta = "Serviço", Probabilidade > 0.5, GP not empty,
 * e soma dos meses do período > 0.
 * Zera, nas linhas mantidas, qualquer mês fora do período escolhido — mesmo que a
 * planilha original tivesse valores neles (a importação só deve carregar os meses
 * do período selecionado).
 * Returns a Promise resolving to an array of row objects.
 *
 * @param {File} file
 * @param {'ANO'|'SEMESTRE'|'TRIMESTRE'} tipoPeriodo
 * @param {number|string} [periodoNumero] - ignorado para tipoPeriodo='ANO'
 */
export function parseForecastExcel(file, tipoPeriodo, periodoNumero) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        const mesesDoPeriodo = getMesesDoPeriodo(tipoPeriodo, periodoNumero);

        reader.onload = (e) => {
            try {
                const wb   = XLSX.read(e.target.result, { type: 'array' });
                const ws   = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

                const headerIdx = findHeaderRowIndex(data);
                if (headerIdx === -1) {
                    throw new Error('Cabeçalho não encontrado. Verifique se a planilha possui as colunas "Cliente" e "Oferta".');
                }

                const colIdx = buildColIndex(data[headerIdx]);
                const rows = [];

                for (let i = headerIdx + 1; i < data.length; i++) {
                    const row = data[i];

                    // skip empty rows
                    if (!row.some(c => c !== '')) continue;

                    const parsed = parseRow(row, colIdx, i);

                    if (parsed.oferta !== 'Serviço')       continue;
                    if (parsed.probabilidade <= 0.5)        continue;
                    if (!parsed.gp)                         continue;

                    const somaPeriodo = mesesDoPeriodo.reduce(
                        (sum, mes) => sum + (Number(parsed.meses[mes]) || 0), 0
                    );
                    if (somaPeriodo <= 0) continue;

                    // Receita Bruta e Ponderada passam a refletir só os meses do período,
                    // ignorando as colunas "Valor Total Ano" e "Soma Ponderada" da planilha
                    // (que consideram o ano todo). Margem Líquida segue como veio.
                    parsed.somaTotal = somaPeriodo;
                    parsed.somaPonderada = somaPeriodo * parsed.probabilidade;

                    // Zera meses fora do período escolhido.
                    MONTHS.forEach(({ key }) => {
                        if (!mesesDoPeriodo.includes(key)) parsed.meses[key] = 0;
                    });

                    rows.push(parsed);
                }

                resolve(rows);
            } catch (err) {
                reject(err);
            }
        };

        reader.onerror = () => reject(new Error('Erro ao ler o arquivo.'));
        reader.readAsArrayBuffer(file);
    });
}
